import { FC } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

/**
 * Renders the frozen assignment explanation as two independently-expandable sections:
 * "why this advisor" and "why not the other rules". They answer different questions and
 * are read at different moments, so they collapse separately.
 *
 * Driven by the stored TEXT rather than re-deriving copy from the trace: the Spanish label
 * maps live in the agent package, and `@agent/*` is a TypeScript-only alias (no runtime
 * imports), so re-rendering here would mean duplicating those labels and letting them drift
 * from what was actually frozen at decision time.
 */
type AssignmentRationaleDetailProps = {
  text: string;
  className?: string;
  /**
   * "brand" matches the inbox, where purple marks the disclosure as an action.
   * "neutral" matches the tablero profile panel, whose section headers are all
   * neutral — a purple trigger there would read as the only action on a panel of
   * read-only data, which is exactly the extra voice the design system forbids.
   */
  tone?: "brand" | "neutral";
};

const WHY_NOT_HEADING = "Por qué no otras reglas:";

type Parsed = {
  whyBlocks: { heading?: string; lines: string[]; bullets: string[]; isWarning: boolean }[];
  whyNotBullets: string[];
};

const parse = (text: string): Parsed => {
  const blocks = text.split("\n\n").slice(1); // block 0 is the headline, shown by the caller
  const whyNotIndex = blocks.findIndex((b) => b.startsWith(WHY_NOT_HEADING));

  const whySource = whyNotIndex === -1 ? blocks : blocks.slice(0, whyNotIndex);
  const whyNotSource = whyNotIndex === -1 ? "" : blocks[whyNotIndex];

  const whyBlocks = whySource
    .map((block) => {
      const all = block.split("\n").filter((l) => l.trim().length > 0);
      const bullets = all.filter((l) => l.startsWith("• ")).map((l) => l.slice(2));
      const rest = all.filter((l) => !l.startsWith("• "));
      const heading = rest[0]?.endsWith(":") ? rest[0] : undefined;
      return {
        heading,
        lines: heading ? rest.slice(1) : rest,
        bullets,
        isWarning: block.startsWith("⚠️"),
      };
    })
    .filter((b) => b.heading || b.lines.length || b.bullets.length);

  const whyNotBullets = whyNotSource
    .split("\n")
    .filter((l) => l.startsWith("• "))
    .map((l) => l.slice(2));

  return { whyBlocks, whyNotBullets };
};

/** Splits "Zona — el inmueble X está en Y" into a bolded label and its explanation. */
const RationaleRow: FC<{ text: string }> = ({ text }) => {
  const separator = text.indexOf(" — ");
  if (separator === -1) {
    return <p className="leading-relaxed text-muted-foreground">{text}</p>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-medium text-foreground">{text.slice(0, separator)}</span>
      <span className="leading-relaxed text-muted-foreground">
        {text.slice(separator + 3)}
      </span>
    </div>
  );
};

const AssignmentRationaleDetail: FC<AssignmentRationaleDetailProps> = ({
  text,
  className,
  tone = "brand",
}) => {
  const { whyBlocks, whyNotBullets } = parse(text);

  if (!whyBlocks.length && !whyNotBullets.length) return null;

  const triggerClass = cn(
    "py-2 text-xs font-semibold hover:no-underline hover:opacity-70",
    tone === "brand" ? "text-palomma-primary" : "text-neutral-900"
  );

  return (
    <Accordion type="multiple" className={cn("w-full text-left text-sm", className)}>
      {whyBlocks.length > 0 && (
        <AccordionItem value="why" className="border-b-0">
          <AccordionTrigger className={triggerClass}>Por qué se asignó</AccordionTrigger>
          <AccordionContent className="pb-3">
            <div className="flex flex-col gap-3">
              {whyBlocks.map((block, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex flex-col gap-2",
                    block.isWarning &&
                      "rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900"
                  )}
                >
                  {block.heading && (
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {block.heading.replace(/:$/, "")}
                    </p>
                  )}
                  {block.lines.map((line, j) => (
                    <p key={j} className="leading-relaxed text-muted-foreground">
                      {line}
                    </p>
                  ))}
                  {block.bullets.length > 0 && (
                    <div className="flex flex-col gap-2">
                      {block.bullets.map((bullet, j) => (
                        <RationaleRow key={j} text={bullet} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {whyNotBullets.length > 0 && (
        <AccordionItem value="whyNot" className="border-b-0">
          <AccordionTrigger className={triggerClass}>
            Por qué no otras reglas ({whyNotBullets.length})
          </AccordionTrigger>
          <AccordionContent className="pb-3">
            <div className="flex flex-col gap-3">
              {whyNotBullets.map((bullet, i) => (
                <RationaleRow key={i} text={bullet} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );
};

export default AssignmentRationaleDetail;
